import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import formativeService from 'services/formative';
import { v4 as uuid } from 'uuid';

const PageContext = createContext();

const PageProvider = (props) => {
    const { course, currentAssignmentId, currentAssignment, setFormative } = useApp();
    const { auth } = useAuth();
    const history = useHistory();
    const { message } = useSnackbar();
    const { t } = useTranslation();

    const [form, _setForm] = useState(null);
    const [errors, setErrors] = useState({ topic: '' });
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const hasAdditionalSettings = useMemo(
        () =>
            ['rubric', 'system'].includes(
                currentAssignment?.ai_student_feedback_settings?.structure ??
                    currentAssignment?.ai_feedback_settings?.structure
            ),
        [currentAssignment?.ai_feedback_settings?.structure, currentAssignment?.ai_student_feedback_settings?.structure]
    );

    const validateAll = useCallback(() => {
        const isTopicValid = Boolean(form?.topic);
        setErrors({ topic: !isTopicValid ? t('error.required-field', 'This field is required.') : '' });
        return isTopicValid;
    }, [form?.topic, t]);

    const setForm = useCallback(
        (param) => {
            if (hasClickedSubmit) validateAll();
            _setForm(param);
        },
        [hasClickedSubmit, validateAll]
    );

    const onClickBack = () => {
        history.push(`/assignments/${currentAssignmentId}`);
    };

    const onClickSubmit = async () => {
        setHasClickedSubmit(true);
        const isValid = validateAll();
        if (!isValid) return;
        const timestamp = new Date().getTime();
        const formative = {
            id: uuid(),
            active: true,
            created_on: timestamp,
            modified_on: timestamp,
            owner_account_id: auth.id,
            course_id: course.id,
            assignment_id: currentAssignmentId,
            ai_context: form.topic,
            ...(form.settings && {
                ai_student_feedback_settings: {
                    suggestion_length: form.settings.suggestionLength,
                    language_level: form.settings.languageLevel,
                    tone: form.settings.tone,
                },
            }),
        };
        try {
            setIsLoading(true);
            await formativeService.createFormative(formative);
            setFormative(formative);
            const params = new URLSearchParams({
                owner_account_id: auth.id,
                type: 'formative',
            });
            history.push(`/assignments/${currentAssignmentId}/submissions?${params}`);
        } catch (e) {
            message.error(t('common:message.something-went-wrong', 'Something went wrong.'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!currentAssignment) return;
        _setForm({
            topic: '',
            settings: hasAdditionalSettings
                ? {
                      suggestionLength:
                          currentAssignment.ai_student_feedback_settings?.suggestion_length ??
                          currentAssignment.ai_feedback_settings.suggestion_length,
                      languageLevel:
                          currentAssignment.ai_student_feedback_settings?.language_level ??
                          currentAssignment.ai_feedback_settings.language_level,
                      tone:
                          currentAssignment.ai_student_feedback_settings?.tone ??
                          currentAssignment.ai_feedback_settings.tone,
                  }
                : null,
        });
    }, [currentAssignment, hasAdditionalSettings]);

    return (
        <PageContext.Provider
            value={{ currentAssignment, form, setForm, errors, isLoading, onClickBack, onClickSubmit }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
